import { isAxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';

import { AuthenticationResponse, isNil } from '@activepieces/shared';

import { authenticationApi } from './authentication-api';
import { redirectToSignIn } from './auth-navigation';
import { isInternalAdminPage } from './internal-admin-area';
import { sentry } from './sentry';

const tokenKey = 'token';
const currentUserKey = 'currentUser';

// Inside the internal admin, the Khub user session stays invisible
// (see internal-admin-area.ts).
const readUserSession = (key: string): string | null =>
  isInternalAdminPage() ? null : localStorage.getItem(key);

export const authenticationSession = {
  saveResponse(response: AuthenticationResponse) {
    localStorage.setItem(tokenKey, response.token);
    localStorage.setItem(
      currentUserKey,
      JSON.stringify({
        ...response,
        token: undefined,
      }),
    );

    sentry.setUser(response);

    window.dispatchEvent(new Event('storage'));
  },
  restoreSentryUser() {
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      sentry.setUser(currentUser);
    }
  },
  getToken(): string | null {
    return readUserSession(tokenKey);
  },
  getProjectId(): string | null {
    const token = this.getToken();
    if (isNil(token)) {
      return null;
    }
    const decodedJwt = jwtDecode<{ projectId: string }>(token);
    return decodedJwt.projectId;
  },
  get tenantId(): string | null {
    const token = this.getToken();
    if (isNil(token)) {
      return null;
    }
    const decodedJwt = jwtDecode<{ tenantId: string }>(token);
    return decodedJwt.tenantId;
  },
  appendProjectRoutePrefix(path: string): string {
    const projectId = this.getProjectId();
    if (isNil(projectId)) {
      return path;
    }
    return `/projects/${projectId}${path.startsWith('/') ? path : `/${path}`}`;
  },
  getPlatformId(): string | null {
    return this.getCurrentUser()?.platformId ?? null;
  },
  getUserPlatformRole() {
    return this.getCurrentUser()?.platformRole ?? null;
  },
  async switchToPlatform(platformId: string) {
    if (authenticationSession.getPlatformId() === platformId) {
      return;
    }
    const result = await authenticationApi.switchPlatform({
      platformId,
    });
    localStorage.setItem(tokenKey, result.token);
    localStorage.setItem(
      currentUserKey,
      JSON.stringify({
        ...this.getCurrentUser(),
        platformId,
      }),
    );
    window.location.href = '/';
  },
  async switchToSession(projectId: string) {
    if (authenticationSession.getProjectId() === projectId) {
      return;
    }
    const result = await authenticationApi.switchProject({ projectId });
    localStorage.setItem(tokenKey, result.token);
    localStorage.setItem(
      currentUserKey,
      JSON.stringify({
        ...this.getCurrentUser(),
        projectId,
      }),
    );
    window.dispatchEvent(new Event('storage'));
  },
  async switchToTenant(tenantId: string) {
    if (this.tenantId === tenantId) {
      return;
    }
    const result = await authenticationApi.switchTenant({ tenantId });
    localStorage.setItem(tokenKey, result.token);
    localStorage.setItem(
      currentUserKey,
      JSON.stringify({
        ...this.getCurrentUser(),
        tenantId,
        platformId: result.platformId,
        projectId: result.projectId,
      }),
    );
    window.location.href = '/';
  },
  isLoggedIn(): boolean {
    return !!this.getToken() && !!this.getCurrentUser();
  },
  clearSession() {
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(currentUserKey);
    sentry.clearUser();
  },
  logOut() {
    authenticationApi.signOut().catch((error) => {
      if (isAxiosError(error) && error.response?.status === 401) return;
      sentry.captureException(error, 'Server-side sign-out failed during logout — token blacklist and refresh token revocation may not have been applied');
    });
    this.clearSession();
    redirectToSignIn();
  },
  getCurrentUser(): AuthenticationResponse | null {
    const user = readUserSession(currentUserKey);
    if (user) {
      try {
        return JSON.parse(user);
      } catch (e) {
        sentry.captureException(e, 'Corrupted user session in localStorage', {
          extra: {
            corruptedValue: user.substring(0, 100),
          },
        });
        return null;
      }
    }
    return null;
  },
};
